import { useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { OutputFormat, suggestOutputPrefix } from "./ExportWizardState";

interface Props {
  value: string;
  onChange: (v: string) => void;
  outputFormat: OutputFormat;
  onOutputFormatChange: (f: OutputFormat) => void;
  defaultBucket: string | null;
  userEmail: string | null;
}

// Step 4 — output destination + format.
// Auto-suggested prefix when the deployer configured
// PAIM_DEFAULT_OUTPUT_BUCKET (surfaced via /api/me); user can edit.
// Format picker drives the C# exporter's output shape; condensed-json
// is what unlocks the search-env feature.

interface FormatOption {
  value: OutputFormat;
  label: string;
  description: string;
  badge?: string;
}

const FORMAT_OPTIONS: FormatOption[] = [
  {
    value: "original",
    label: "Original documents, zipped (XML/JSON)",
    description:
      "One all.zip per month with every matched document as its own file, in its original format — broadcast pages as XML, podcast episodes as JSON — never rewritten, kept under its source filename, plus a manifest.parquet mapping each entry. Best for downstream pipelines that already speak the source format.",
  },
  {
    value: "ndjson",
    label: "NDJSON (per-page)",
    description:
      "Gzipped NDJSON, one JSON record per page. Useful for ingestion into existing analytics pipelines that consume line-delimited JSON. Can also be loaded into a temporary search environment for ad-hoc querying.",
    badge: "Enables analysis",
  },
  {
    value: "condensed-json",
    label: "Combined NDJSON (program-aligned)",
    description:
      "Gzipped NDJSON, one record per program (pages combined). Best for program-level analysis — each record carries the full transcript of one broadcast. Loads into a temporary search environment.",
    badge: "Enables analysis",
  },
];

export function StepOutput({
  value,
  onChange,
  outputFormat,
  onOutputFormatChange,
  defaultBucket,
  userEmail,
}: Props) {
  // Seed an auto-suggestion the first time this step renders with an
  // empty value. We don't overwrite a user's edit on subsequent
  // mounts — the wizard state is the source of truth once anything
  // has been typed.
  useEffect(() => {
    if (!value && defaultBucket) {
      onChange(suggestOutputPrefix({ defaultBucket, userEmail }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const looksValid = value.startsWith("s3://") && value.endsWith("/");

  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        Destination
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Where the export should land. Each month writes into its own
        subdirectory under this prefix (e.g.{" "}
        <code>
          {value || "s3://…/"}
          <wbr />
          2025-04/
        </code>
        ) so each month is a self-describing sub-archive.
      </Typography>
      <TextField
        fullWidth
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="s3://my-bucket/exports/job-name/"
        sx={{
          fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
        }}
      />
      {defaultBucket && (
        <Button
          size="small"
          sx={{ mt: 1 }}
          onClick={() =>
            onChange(suggestOutputPrefix({ defaultBucket, userEmail }))
          }
        >
          Re-suggest a fresh prefix
        </Button>
      )}
      {value && !looksValid && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Output prefix should start with <code>s3://</code> and end with{" "}
          <code>/</code> (it's a directory, not a single file).
        </Alert>
      )}

      <Typography variant="overline" color="text.secondary" sx={{ mt: 4, display: "block" }}>
        Format
      </Typography>
      <RadioGroup
        value={outputFormat}
        onChange={(_, v) => onOutputFormatChange(v as OutputFormat)}
        sx={{ mt: 1 }}
      >
        {FORMAT_OPTIONS.map((opt) => (
          <FormControlLabel
            key={opt.value}
            value={opt.value}
            control={<Radio />}
            sx={{ alignItems: "flex-start", mb: 1.5, mx: 0 }}
            label={
              <Box sx={{ pt: 0.5 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body1">{opt.label}</Typography>
                  {opt.badge && (
                    <Box
                      component="span"
                      sx={{
                        fontSize: "0.7rem",
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 0.5,
                        backgroundColor: "primary.main",
                        color: "primary.contrastText",
                        fontWeight: 600,
                        letterSpacing: 0.5,
                      }}
                    >
                      {opt.badge}
                    </Box>
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {opt.description}
                </Typography>
              </Box>
            }
          />
        ))}
      </RadioGroup>
    </Box>
  );
}
